'use client';

import { useCouponPreviewCoupon } from '@ps/features/coupons/contexts/preview/context';
import { couponPreviewPrice } from '@ps/features/coupons/functions/couponPreviewPrice';
import currency from 'currency.js';

import { usePdpPurchase } from '../contexts/purchase/context';
import {
  type ListingPriceInfo,
  PURCHASED_DIGITAL_CTA_LABEL,
} from '../functions/listingDisplay';

/**
 * The inline "Download Now for $X" / "Get it Now" CTA in the digital-summary
 * row. Triggers the SAME shared purchase/download flow as the sticky-footer CTA
 * (RN wires the summary row's `onDownload` to the one `onPurchase`), and prices
 * its label the same way — the `?coupon=` preview included — so the two buttons
 * never quote different amounts.
 *
 * Outside `PdpPurchaseProvider` (the digital-product editor's read-only landing
 * preview) there's no purchase context — the button stays a non-navigating stub,
 * matching the preview's inert CTAs.
 */
export function DigitalSummaryDownloadButton({
  priceInfo,
  freeLabel,
}: {
  priceInfo: ListingPriceInfo;
  freeLabel: string;
}) {
  const purchase = usePdpPurchase();
  const coupon = useCouponPreviewCoupon();
  const couponPrice = couponPreviewPrice(
    { listPrice: priceInfo.price, currentPrice: priceInfo.effectivePrice },
    coupon,
  );
  const label = priceInfo.isFree
    ? freeLabel
    : `Download Now for ${currency(couponPrice ?? priceInfo.effectivePrice).format()}`;
  return (
    <button
      type="button"
      onClick={purchase?.onPurchase}
      disabled={purchase != null && !purchase.isReady}
      className="text-label mt-1 inline-flex h-8 min-w-[181px] max-w-[250px] items-center justify-center self-start rounded-lg bg-[#1A1A1A] px-4 font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
    >
      {purchase?.isPurchased ? PURCHASED_DIGITAL_CTA_LABEL : label}
    </button>
  );
}
